import type { Entry } from "./types";
import type { OfflineOperation } from "./api";

const DATABASE = "memohub-offline-v1";
const STORE = "encrypted";
const KEY_STORE = "keys";
const SESSION_KEY = "memohub_offline_session";
const SESSION_RECORD = "session";

interface EncryptedRecord { key: string; iv: number[]; data: ArrayBuffer }
interface SharedSession { id: string; subject?: string }
export interface OfflineSession { subject: string; revision: string }
export class OfflineSessionEndedError extends Error {
  constructor() { super("Die MemoHub-Sitzung wurde beendet."); }
}
let activeSession: OfflineSession | null = null;
const logoutListeners = new Set<() => void>();

export const offlineSessionRevision = (): string | null => localStorage.getItem(SESSION_KEY);
export function assertOfflineRevision(expected: string | null): void {
  if (offlineSessionRevision() !== expected) throw new OfflineSessionEndedError();
}
function sharedSession(revision: string | null): SharedSession | null {
  if (!revision) return null;
  try {
    const value = JSON.parse(revision) as SharedSession;
    return typeof value.id === "string" ? value : null;
  } catch { return null; }
}
function notifyLogout(): void {
  activeSession = null;
  for (const listener of logoutListeners) listener();
}
function checkSharedSession(): void {
  if (activeSession && activeSession.revision !== offlineSessionRevision()) notifyLogout();
}
window.addEventListener("storage", (event) => {
  if (event.key === SESSION_KEY || event.key === null) checkSharedSession();
});
window.addEventListener("pageshow", checkSharedSession);
window.addEventListener("focus", checkSharedSession);
document.addEventListener("visibilitychange", checkSharedSession);
export function subscribeOfflineLogout(listener: () => void): () => void {
  logoutListeners.add(listener);
  return () => { logoutListeners.delete(listener); };
}
export function captureOfflineSession(subject?: string): OfflineSession {
  checkSharedSession();
  if (!activeSession || (subject && activeSession.subject !== subject)) throw new OfflineSessionEndedError();
  return activeSession;
}
export function assertOfflineSession(expected: OfflineSession): void {
  if (captureOfflineSession(expected.subject) !== expected) throw new OfflineSessionEndedError();
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 2);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: "key" });
      if (!request.result.objectStoreNames.contains(KEY_STORE)) request.result.createObjectStore(KEY_STORE);
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);
  });
}
function completed(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new OfflineSessionEndedError());
    transaction.onerror = () => reject(transaction.error);
  });
}

// Called only after IDHub session verification. The captured revision fences
// authentication responses that arrive after a logout in any tab.
export async function beginOfflineSession(subject: string, expectedRevision: string | null): Promise<void> {
  assertOfflineRevision(expectedRevision);
  const previous = sharedSession(expectedRevision);
  const revision = previous?.subject === subject
    ? expectedRevision!
    : JSON.stringify({ id: crypto.randomUUID(), subject } satisfies SharedSession);
  const adoptLegacyCache = !expectedRevision && localStorage.getItem("memohub_subject") === subject;
  localStorage.setItem(SESSION_KEY, revision);
  const lease: OfflineSession = { subject, revision };
  activeSession = lease;
  const database = await openDatabase();
  try {
    assertOfflineSession(lease);
    const transaction = database.transaction([STORE, KEY_STORE], "readwrite");
    const done = completed(transaction);
    const store = transaction.objectStore(STORE);
    const request = store.get(SESSION_RECORD);
    request.onsuccess = () => {
      try {
        assertOfflineSession(lease);
        if (request.result?.revision !== revision && !(adoptLegacyCache && !request.result)) {
          store.clear();
          transaction.objectStore(KEY_STORE).clear();
        }
        store.put({ key: SESSION_RECORD, revision });
      } catch { transaction.abort(); }
    };
    await done;
    assertOfflineSession(lease);
  } finally { database.close(); }
}

export async function resumeOfflineSession(subject: string, revision: string | null): Promise<void> {
  assertOfflineRevision(revision);
  if (sharedSession(revision)?.subject !== subject) throw new OfflineSessionEndedError();
  const lease = { subject, revision: revision! };
  activeSession = lease;
  await withSessionStores(lease, "readonly", () => {});
}

// The shared fence and the data operation participate in one IDB transaction.
// A suspended tab cannot recreate keys or ciphertext after another tab clears it.
async function withSessionStores(lease: OfflineSession, mode: IDBTransactionMode, operation: (transaction: IDBTransaction) => void): Promise<void> {
  assertOfflineSession(lease);
  const database = await openDatabase();
  try {
    assertOfflineSession(lease);
    const transaction = database.transaction([STORE, KEY_STORE], mode);
    const done = completed(transaction);
    const request = transaction.objectStore(STORE).get(SESSION_RECORD);
    request.onsuccess = () => {
      try {
        assertOfflineSession(lease);
        if (request.result?.revision !== lease.revision) throw new OfflineSessionEndedError();
        operation(transaction);
      } catch { transaction.abort(); }
    };
    await done;
    assertOfflineSession(lease);
  } finally { database.close(); }
}
async function key(lease: OfflineSession): Promise<CryptoKey> {
  const generated = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  let result = generated;
  await withSessionStores(lease, "readwrite", (transaction) => {
    const store = transaction.objectStore(KEY_STORE);
    const request = store.get(lease.subject);
    request.onsuccess = () => {
      if (request.result) result = request.result;
      else store.put(generated, lease.subject);
    };
  });
  return result;
}
async function writeEncrypted(recordKey: string, lease: OfflineSession, value: unknown): Promise<void> {
  const encryptionKey = await key(lease);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, encryptionKey, new TextEncoder().encode(JSON.stringify(value)));
  await withSessionStores(lease, "readwrite", (transaction) => {
    transaction.objectStore(STORE).put({ key: recordKey, iv: Array.from(iv), data } satisfies EncryptedRecord);
  });
}
async function readEncrypted<T>(recordKey: string, lease: OfflineSession, empty: T): Promise<T> {
  let record: EncryptedRecord | undefined;
  let encryptionKey: CryptoKey | undefined;
  await withSessionStores(lease, "readonly", (transaction) => {
    const recordRequest = transaction.objectStore(STORE).get(recordKey);
    const keyRequest = transaction.objectStore(KEY_STORE).get(lease.subject);
    recordRequest.onsuccess = () => { record = recordRequest.result; };
    keyRequest.onsuccess = () => { encryptionKey = keyRequest.result; };
  });
  if (!record) return empty;
  if (!encryptionKey) throw new Error("Schlüssel für Offline-Daten fehlt.");
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: new Uint8Array(record.iv) }, encryptionKey, record.data);
  assertOfflineSession(lease);
  return JSON.parse(new TextDecoder().decode(plain));
}
export async function cacheEntries(subject: string, entries: Entry[]): Promise<void> {
  await writeEncrypted(`entries:${subject}`, captureOfflineSession(subject), entries);
}
export const readCachedEntries = (subject: string) => readEncrypted<Entry[]>(`entries:${subject}`, captureOfflineSession(subject), []);
export const readOfflineOperations = (subject: string) => readEncrypted<OfflineOperation[]>(`operations:${subject}`, captureOfflineSession(subject), []);
export async function queueOfflineOperation(subject: string, operation: OfflineOperation): Promise<void> {
  const lease = captureOfflineSession(subject);
  const operations = await readEncrypted<OfflineOperation[]>(`operations:${subject}`, lease, []);
  operations.push(operation);
  await writeEncrypted(`operations:${subject}`, lease, operations);
}
export async function clearOfflineOperations(subject: string): Promise<void> {
  await writeEncrypted(`operations:${subject}`, captureOfflineSession(subject), []);
}

export async function clearOfflineData(): Promise<void> {
  const previousRevision = offlineSessionRevision();
  if (activeSession && activeSession.revision !== previousRevision) {
    notifyLogout();
    throw new OfflineSessionEndedError();
  }
  // Invalidate memory and storage synchronously, before any asynchronous cleanup.
  // Even failed cleanup cannot leave the old offline session usable.
  const revokedRevision = JSON.stringify({ id: crypto.randomUUID() } satisfies SharedSession);
  localStorage.setItem(SESSION_KEY, revokedRevision);
  localStorage.removeItem("memohub_subject");
  localStorage.removeItem("memohub_display_name");
  notifyLogout();
  const database = await openDatabase();
  try {
    const transaction = database.transaction([STORE, KEY_STORE], "readwrite");
    const done = completed(transaction);
    const store = transaction.objectStore(STORE);
    const request = store.get(SESSION_RECORD);
    request.onsuccess = () => {
      // A new login may already have installed a different session. Never erase it.
      if (!request.result || request.result.revision === previousRevision) {
        store.clear();
        transaction.objectStore(KEY_STORE).clear();
      }
    };
    await done;
  } finally { database.close(); }
  if (offlineSessionRevision() === revokedRevision) {
    for (const cache of await caches.keys()) await caches.delete(cache);
  }
}
